// ============================================
// TOAST MANAGER
// ============================================

import Alpine from 'alpinejs';
import type { Toast, ToastAction } from '../../types';
import toastContainerTemplate from '../../templates/toast-container.html?raw';

export function registerToastManager(): void {
    Alpine.data('toastManager', () => ({
        toasts: [] as Toast[],

        show(message: string, type: string = 'default', duration: number = 4000, action: ToastAction | null = null, title?: string): number {
            const id = Date.now() + Math.random();

            // Unknown types fall back to the plain one, so every toast has its icon.
            if (!['default', 'info', 'success', 'danger', 'error', 'warning'].includes(type)) {
                type = 'default';
            }

            const iconMap: { [key: string]: string | null } = {
                'default': null,
                'info': 'info',
                'success': 'check_circle',
                'danger': 'error',
                'error': 'error',
                'warning': 'warning'
            };

            const toast: Toast = {
                id,
                title,
                message,
                type,
                duration,
                action,
                icon: iconMap[type] || null,
                visible: false,
                startTime: Date.now(),
                remainingTime: duration,
                isPaused: false
            };
            this.toasts.push(toast);

            // Show toast in next tick to trigger enter animation
            setTimeout(() => {
                const t = this.toasts.find(t => t.id === id);
                if (t) t.visible = true;
            }, 100);

            if (duration > 0) {
                const timeoutId = window.setTimeout(() => {
                    this.remove(id);
                }, duration + 100);
                toast.timeoutId = timeoutId;
            }

            return id;
        },

        pauseToast(id: number): void {
            const toast = this.toasts.find(t => t.id === id);
            // Don't pause if there's no duration (infinite toast)
            if (toast && toast.duration > 0 && !toast.isPaused && toast.timeoutId) {
                clearTimeout(toast.timeoutId);
                // Calculate elapsed time since last start/resume
                const elapsed = Date.now() - (toast.startTime || 0);
                // Subtract elapsed from current remaining time
                toast.remainingTime = Math.max(0, (toast.remainingTime || toast.duration) - elapsed);
                toast.isPaused = true;
            }
        },

        resumeToast(id: number): void {
            const toast = this.toasts.find(t => t.id === id);
            // Don't resume if there's no duration (infinite toast)
            if (toast && toast.duration > 0 && toast.isPaused && toast.remainingTime && toast.remainingTime > 0) {
                // Record new start time for next pause calculation
                toast.startTime = Date.now();
                toast.isPaused = false;
                // Set new timeout with remaining time
                const timeoutId = window.setTimeout(() => {
                    this.remove(id);
                }, toast.remainingTime);
                toast.timeoutId = timeoutId;
            }
        },

        remove(id: number): void {
            const toast = this.toasts.find(t => t.id === id);
            if (toast) {
                if (toast.timeoutId) {
                    clearTimeout(toast.timeoutId);
                }
                toast.visible = false;
                // Remove from array after transition completes (200ms + 50ms buffer)
                setTimeout(() => {
                    this.toasts = this.toasts.filter(t => t.id !== id);
                }, 250);
            }
        },

        handleAction(toast: Toast): void {
            if (toast.action && toast.action.callback) {
                toast.action.callback();
            }
            this.remove(toast.id);
        },

        success(message: string, duration: number = 4000, action: ToastAction | null = null): number {
            return this.show(message, 'success', duration, action);
        },

        error(message: string, duration: number = 4000, action: ToastAction | null = null): number {
            return this.show(message, 'danger', duration, action);
        },

        warning(message: string, duration: number = 4000, action: ToastAction | null = null): number {
            return this.show(message, 'warning', duration, action);
        },

        info(message: string, duration: number = 4000, action: ToastAction | null = null): number {
            return this.show(message, 'info', duration, action);
        }
    }));
}

export function injectToastContainer(): void {
    const toastContainer = document.createElement('div');
    toastContainer.setAttribute('x-data', 'toastManager()');
    toastContainer.setAttribute('x-on:md-toast.window', 'show($event.detail.message, $event.detail.type, $event.detail.duration, $event.detail.action, $event.detail.title)');

    // Tailwind classes for toast container - fixed bottom center
    toastContainer.className = 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[99999] max-w-md w-full px-4 space-y-3 pointer-events-none';

    const bottomNav = document.querySelector('.bottom-nav');
    if (bottomNav) {
        // Adjust position if bottom nav exists
        toastContainer.style.bottom = '73px'; // 1rem (16px) + 56px (bottom nav) + space
    }

    // Use imported HTML template
    toastContainer.innerHTML = toastContainerTemplate;

    document.body.appendChild(toastContainer);
}
